import { extend } from "vee-validate";
import { i18n } from "@/locales/index.js";
import grdService from "@/services/grdService";
import configurationService from "@/services/configurationService";
import store from "@/store/"

import {
  length,
  numeric,
  required,
  email,
  min,
  max,
  max_value,
  between
} from "vee-validate/dist/rules";

extend("required", {
  ...required,
  message: (_, values) => i18n.t('Validation.required')
});

extend("true", {
  validate(value) {
    return value === 1 || value === true
  },
  message: (_, values) => i18n.t('Validation.required')
})

extend("specialChar", {
  validate(value, args) {
    if(args.type == "street") // "'" is needed for the street, not for the number nor the box
      return value.match(/^[a-zA-Z0-9 \-àáâäçèéêëìíîïñòóôöùúûü'.]*$/i);
    else if(args.type == "number-box")
      return value.match(/^[a-zA-Z0-9 \-àáâäçèéêëìíîïñòóôöùúûü.]*$/i);
  },
  params: ['type'],
  message: (_, values) => i18n.t('Validation.specialChar')
})

extend("alpha", {
  validate(value) {
    return value.match(/^[a-zA-Z \-àáâäçèéêëìíîïñòóôöùúûü'.]*$/i)
  },
  message: (_, values) => i18n.t('Validation.alpha')
})

extend("numeric", {
  ...numeric,
  message: (_, values) => i18n.t('Validation.numeric')
});

extend("min", {
  ...min,
  message: (_, values) => i18n.t('Validation.min')
});

extend("max", {
  ...max,
  message: (_, params, values) => i18n.tc('Validation.max', params.length)
});

extend("max_value", {
  ...max_value,
  message: (_, params, values) => i18n.t('Validation.max_value', {max: params.max})
});

extend("length", {
  ...length,
  message: (_, values) => i18n.t('Validation.length')
});

extend("email", {
  ...email,
  message: (_, values) => i18n.t('Validation.email')
});

extend("specialchars", {
  validate(value) {
    let regex = new RegExp("^[^<>'=!?`´+~%]+$");
    return regex.test(value)
  },
  message: field => i18n.t('Validation.email')
});

extend("noemail", {
  validate(value) {
    return value !== 'noemail@mega.be';
  },
  message: (_, values) => i18n.t('Validation.noemail')
})

extend("float", {
  validate(value) {
    return !isNaN(parseFloat(value)) && isFinite(value);
  },
  message: (_, values) => i18n.t('Validation.float')
});

extend("sponsor", {
  validate(value) {
    return value.match(/^ME[0-9]{6}$/i);
  },
  message: (_, values) => i18n.t('Validation.sponsor_customer_id')
});

extend("between", {
  ...between,
  message: (_, values) => i18n.t('Validation.between', {min: values.min, max: values.max})
});

extend("adult1", {
  validate(value) {
    let minor = new Date();
    minor.setHours(0, 0, 0, 0);
    minor.setFullYear(minor.getFullYear() - 18);

    let senior = new Date();
    senior.setHours(0, 0, 0, 0);
    senior.setFullYear(senior.getFullYear() - 18);

    return new Date(value) <= minor && new Date(value) >= senior;
  },
  message: (_, values) => i18n.t('Validation.adult1')
});

extend("be-phone", {
  validate(value) {
    return value.number.match("^((\\+|00|)32\\s?|0|)4(5[56]|60|[6789]\\d)(\\s?\\d{2}){3}$|^((\\+|00|)32\\s?|0|)(\\d(\s)?\\d{3}|\\d{2}(\s)?\\d{2})((\\s)?\\d{2}){2}$") && parseInt(value.number) > 0;
  },
  message: (_, values) => i18n.t('Validation.phone')
});

extend("phone", {
  validate(value) {
    return !isNaN(value.number) && value.number.toString().length >= 6;
  },
  message: (_, values) => i18n.t('Validation.phone')
});

extend("iban", {
  validate(value) {
    value = value.replace(/\s/g, '').toUpperCase()
    return value.match(/^BE[0-9]{14}$/i) || value.match(/^(?!BE).+$/i)
  },
  message: (_, values) => i18n.t('Validation.iban')
})

extend("bic", {
  validate(value) {
    return value.match(/([a-zA-Z]{4}[a-zA-Z]{2}[a-zA-Z0-9]{2}([a-zA-Z0-9]{3})?)/i);
  },
  message: (_, values) => i18n.t('Validation.bic')
})

extend("ean", {
  async validate(value) {
    let response = await grdService.getEan(value)
    return response.valid;
  },
  message: (_, values) => i18n.t('Validation.ean')
})

extend("blacklist", async value => {
  if(value.length > 14) {
    let response = await configurationService.checkIban(value.replace(/\s+/g, ""));
    if(response.blacklisted === null) {
      return i18n.t('Validation.iban')
    } else if(response.blacklisted === true) {
      return i18n.t('Validation.ibanBlacklisted')
    }
  }
  return true;
});


extend("noSupplied", {
  async validate(value) {
    return value.region !== 'bx' || store.state.subscription.origin === "loss" || store.state.common.bxAllowed;
  },
  message: (_, values) => i18n.t('Validation.noSupplied')
})

extend("fileExt", {
  validate(value, allowedExt) {
    let ext = value.match(/\.[0-9a-z]+$/i)
    return allowedExt.indexOf(ext[0]) >= 0
  },
  message: (_, values) => i18n.t('Validation.moveFile')
})

extend("vatNumber", {
  validate(value) {
    return value.match(/BE(\d{9,10})/i)
  },
  message: (_, values) => i18n.t('Validation.vat_number')
})

extend("commaFloatNumber", {
  validate(value, { min = null, max = null } = {}) {
    value = value.replace(',', '.');
    const minPart = JSON.parse(min) !== null ? value >= parseFloat(min) : true;
    const maxPart = JSON.parse(max) !== null ? value <= parseFloat(max) : true;
    return !isNaN(parseFloat(value)) && isFinite(value) && minPart && maxPart;
    },
  params: ['min', 'max'],
  message: (_, values) => {
    if(JSON.parse(values.min) && JSON.parse(values.max))
      return i18n.t('Validation.commaFloatNumberMinMax', {min: values.min, max: values.max});
    else if(JSON.parse(values.min))
      return i18n.t('Validation.commaFloatNumberMin', {min: values.min});
    else if(JSON.parse(values.max))
      return i18n.t('Validation.commaFloatNumberMax', {max: values.max});
    else
      return i18n.t('Validation.commaFloatNumber')
  }
})

extend("refAgent", {
  validate(value) {
    return value.match(/^[a-zA-Z0-9 \-àáâäçèéêëìíîïñòóôöùúûü.]*$/i)
  },
  message: (_, values) => i18n.t('Validation.specialChar')
})



// WEBPACK FOOTER //
// ./src/validations/rules.js